import { evaluateConditions } from './evaluateConditions';
import type { StepFormBranch, WorkflowStep } from './templateTypes';
import type { FieldValue } from './values';

/** A branch rule plus where it sits — the index is what tells the composer which steps
 *  are the retained prefix and which get replaced. */
export type ResolvedFormBranch = {
  branch: StepFormBranch;
  /** Index of the step carrying the rule, within the list it was resolved against. */
  stepIndex: number;
};

/**
 * The branch rule this step's CURRENT answers have triggered, or null.
 *
 * Modelled on `findOptionRedirect`, and respects visibility the same way: a field or
 * section hidden by the current values can't have been answered by the advisor, and a
 * stale value left behind a now-hidden condition must not swap the form out from under
 * them. The first match in document order wins — a step can hold two triggered rules
 * (two radios, two branching answers), but only one form can load, and "the first one you
 * meet reading down the page" is the only order the advisor can predict.
 *
 * Matching is by field id + option value and NOT by looking the option up on the field:
 * a rule whose option has since been renamed should stop firing, which is exactly what
 * comparing against the stored answer gives.
 */
export function findStepFormBranch(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): StepFormBranch | null {
  if (step.type !== 'DynamicForm') return null;
  const rules = step.formBranches ?? [];
  if (rules.length === 0) return null;

  for (const section of step.sections ?? []) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    for (const row of section.rows ?? []) {
      for (const col of row.columns ?? []) {
        const field = col.field;
        if (!field || field.type !== 'RadioGroup') continue;
        const value = values[field.id];
        if (typeof value !== 'string' || value === '') continue;
        if (
          (field.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(field.conditions, field.conditionCombinator, values)
        ) {
          continue;
        }
        const match = rules.find(
          (b) => b.fieldId === field.id && b.optionValue === value && b.targetTemplateId,
        );
        if (match) return match;
      }
    }
  }
  return null;
}

/**
 * The branch the advisor's answers have triggered anywhere in this step list, or null.
 *
 * Deliberately does NOT recurse into the target's own steps. Nesting is unsupported by
 * decision, not by oversight: BMO's save contract records a swap as a single boolean, so
 * a two-hop branch would be unresumable — and a cycle would not terminate here.
 */
export function resolveActiveBranch(
  steps: readonly WorkflowStep[],
  values: Record<string, FieldValue>,
): ResolvedFormBranch | null {
  for (const [stepIndex, step] of steps.entries()) {
    const branch = findStepFormBranch(step, values);
    if (branch) return { branch, stepIndex };
  }
  return null;
}

/**
 * Every rule DECLARED on a step list, ignoring the answers.
 *
 * This is what resume replays off. It cannot use {@link resolveActiveBranch}, because the
 * answer that triggered the swap was never persisted: only the selected form's values go
 * to BMO, and the triggering radio belongs to the base form. The saved application carries
 * one boolean saying a swap happened, so the rule itself is the only thing left that can
 * say which form — which is why the convention is one rule per workflow. With more than
 * one declared, the first in document order wins and the rest are unreachable on resume.
 */
export function declaredBranches(steps: readonly WorkflowStep[]): ResolvedFormBranch[] {
  const out: ResolvedFormBranch[] = [];
  steps.forEach((step, stepIndex) => {
    if (step.type !== 'DynamicForm') return;
    for (const branch of step.formBranches ?? []) {
      if (branch.targetTemplateId) out.push({ branch, stepIndex });
    }
  });
  return out;
}
